import { AsyncPipe } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { NewUiButton } from "@ankaadia/ankaadia-shared-frontend";
import { StaticDataModel } from "@ankaadia/graphql";
import { FieldType, FormlyModule } from "@ngx-formly/core";
import { FormlySelectModule } from "@ngx-formly/core/select";
import { Chip } from "primeng/chip";
import { Dialog } from "primeng/dialog";
import { Editor } from "primeng/editor";
import { Message } from "primeng/message";
import { Tooltip } from "primeng/tooltip";
import Quill from "quill";
import { startWith } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { FieldTranslationDialogService } from "../../features/metered-translations/field-translation-dialog.service";
import { TranslationMapping } from "../../features/metered-translations/metered-translations.model";
import { MeteredTranslationsService } from "../../features/metered-translations/metered-translations.service";
import { NewUiMultiselectComponent } from "../../shared/form/multiselect/new-ui-multiselect.component";
import { StaticDataPipe } from "../../shared/static-data/static-data.pipe";
import { TranslateDirective } from "../../shared/transloco/translate.directive";

@Component({
  selector: "new-ui-formly-chips",
  templateUrl: "./new-ui-formly-chips.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormlyModule,
    FormlySelectModule,
    FormsModule,
    StaticDataPipe,
    AsyncPipe,
    Chip,
    Dialog,
    Editor,
    NewUiButton,
    Message,
    Tooltip,
    NewUiMultiselectComponent,
    TranslateDirective,
  ],
})
export class NewUiFormlyChipsComponent extends FieldType implements OnInit {
  translationMapping: TranslationMapping;
  translationText: string;

  constructor(
    protected readonly elementRef: ElementRef,
    private readonly destroyRef: DestroyRef,
    private readonly translationService: MeteredTranslationsService,
    private readonly fieldTranslationDialogService: FieldTranslationDialogService,
    private readonly changeDetector: ChangeDetectorRef
  ) {
    super();
  }

  protected selectedChip: any;
  protected chipSelected: boolean;
  protected selectedOptions: StaticDataModel[];

  private editor: Quill;

  ngOnInit(): void {
    this.field.formControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .pipe(startWith(this.field.formControl.value))
      .subscribe((value) => {
        this.selectedOptions = value?.map((item) => item.experienceField) ?? [];
        this.changeDetector.markForCheck();
      });
  }

  change(value: any, itemValue: any): void {
    if (value.includes(itemValue.value)) {
      this.field.formControl.setValue([
        ...(this.field.formControl.value || []),
        { id: uuidv4(), experienceField: itemValue.value, experienceNotes: null },
      ]);
    } else {
      this.field.formControl.setValue(
        this.field.formControl.value.filter((item) => item.experienceField !== itemValue.value)
      );
    }
    this.field.formControl.markAsDirty();
  }

  remove(item: any): void {
    this.field.formControl.setValue(
      this.field.formControl.value.filter((x) => x.experienceField !== item.experienceField)
    );
    this.field.formControl.markAsDirty();
  }

  clear(): void {
    this.field.formControl.reset([]);
    this.field.formControl.markAsDirty();
  }

  displayOverlay(_event: MouseEvent, field: any): void {
    this.selectedChip = field;
    this.setUpTranslation();
    this.updateEditor(this.editor);
    this.chipSelected = true;
  }

  updateEditor(editor: Quill): void {
    if (editor) {
      this.editor = editor;
      this.editor.setContents(this.selectedChip?.experienceNotes?.delta);
      this.changeDetector.markForCheck();
    }
  }

  updateHtml(): void {
    const value = this.field.formControl.value;
    value.find((item) => item.id === this.selectedChip.id).experienceNotes = {
      contents: this.editor.getText(),
      delta: this.editor.getContents(),
    };
    this.field.formControl.setValue(value);
    this.field.formControl.markAsDirty();
  }

  setUpTranslation(): void {
    this.translationMapping = null;
    this.translationText = null;
    const chipId = this.selectedChip?.id;
    if (!chipId) return null;
    const candidateId = this.translationService.candidatePropertyTranslationsLoaded$.value;
    const currentTranslation = this.translationService.getFieldTranslationByPropertyPath(
      candidateId,
      this.buildPropertyPath(chipId)
    );
    if (currentTranslation) {
      this.translationMapping = { id: currentTranslation.id, formControl: this.field.formControl };
      this.translationText = currentTranslation.targetText;
    }
  }

  private buildPropertyPath(chipId: string): string[] {
    const experienceId = this.field.parent?.model?.id;
    const path = [
      "os",
      "profile",
      "experiences",
      experienceId,
      "experienceFields",
      chipId,
      "experienceNotes",
      "contents",
    ];
    return path;
  }

  openDialog(event: Event): void {
    this.fieldTranslationDialogService.openDialog(
      { sourceText: { delta: this.editor.getContents() }, itemId: this.translationMapping.id },
      event.target as Element
    );
  }
}
